import React from "react";
import { useTranslation } from "react-i18next";
import type { CanvasArtwork } from "../data/canvasArtworks";
import { isCanvasSold } from "../lib/canvasAvailability";
import { useCanvasArtwork } from "../hooks/useTranslatedArtwork";
import { CanvasInvestmentPrice } from "./CanvasInvestmentPrice";

export function CanvasArtworkSpecs({ work }: { work: CanvasArtwork }) {
  const { t } = useTranslation();
  const translated = useCanvasArtwork(work);
  const sold = isCanvasSold(work);

  return (
    <div className="mt-6 rounded-2xl bg-canvas-50/80 p-4 ring-1 ring-canvas-200/70 sm:mt-8 sm:p-6">
      <p className="text-[10px] font-medium uppercase tracking-[0.18em] text-accent">
        {t("canvas.specs.eyebrow")}
      </p>

      <dl className="mt-4 space-y-4">
        <div>
          <dt className="text-xs font-medium uppercase tracking-[0.12em] text-canvas-500">
            {t("canvas.specs.dimensions")}
          </dt>
          <dd className="mt-1 font-display text-base font-semibold text-canvas-950 sm:text-lg">
            {translated.dimensionsFormatted}
          </dd>
          <dd className="mt-2 grid grid-cols-3 gap-2 text-xs text-canvas-600 sm:gap-3 sm:text-sm">
            <div>
              <span className="block text-[10px] uppercase tracking-[0.1em] text-canvas-400">
                {t("canvas.specs.width")}
              </span>
              <span>{translated.dimensions.widthCm}</span>
            </div>
            <div>
              <span className="block text-[10px] uppercase tracking-[0.1em] text-canvas-400">
                {t("canvas.specs.height")}
              </span>
              <span>{translated.dimensions.heightCm}</span>
            </div>
            <div>
              <span className="block text-[10px] uppercase tracking-[0.1em] text-canvas-400">
                {t("canvas.specs.depth")}
              </span>
              <span>{translated.dimensions.depthCm}</span>
            </div>
          </dd>
        </div>

        <div>
          <dt className="text-xs font-medium uppercase tracking-[0.12em] text-canvas-500">
            {t("canvas.specs.medium")}
          </dt>
          <dd className="mt-1 text-sm text-canvas-700">{translated.medium}</dd>
        </div>

        <div>
          <dt className="text-xs font-medium uppercase tracking-[0.12em] text-canvas-500">
            {t("canvas.specs.investment")}
          </dt>
          {sold ? (
            <dd className="mt-2 text-sm leading-relaxed text-canvas-500">
              {t("canvas.sold.cardNote")}
            </dd>
          ) : (
            <>
              <dd className="mt-1">
                <CanvasInvestmentPrice
                  price={translated.investmentPrice}
                  layout="stacked"
                  size="lg"
                />
              </dd>
              <dd className="mt-1 text-sm text-canvas-600">
                {t("canvas.specs.investmentNote")}
              </dd>
            </>
          )}
        </div>

        <div>
          <dt className="text-xs font-medium uppercase tracking-[0.12em] text-canvas-500">
            {t("canvas.specs.availability")}
          </dt>
          <dd className="mt-1 text-sm text-canvas-700">
            {sold ? t("canvas.sold.status") : t("canvas.specs.available")}
          </dd>
        </div>
      </dl>

      <p className="mt-5 border-t border-canvas-200/70 pt-4 text-xs leading-relaxed text-canvas-500">
        {t("canvas.specs.delivery")}
      </p>
    </div>
  );
}
